/**
 * Progressive field help — one-line meaning stays scannable; method
 * detail expands on demand so experts are not flooded with captions.
 */
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useId, useState } from "react";

export interface FieldHelpProps {
	/** Always-visible plain-language meaning (optional). */
	summary?: string | null | undefined;
	/** Deeper method / unit / threshold context (optional). */
	detail?: string | null | undefined;
	/** Start with detail expanded. */
	defaultExpanded?: boolean;
	/** Dense caption under a metric. */
	dense?: boolean;
}

export function FieldHelp({
	summary,
	detail,
	defaultExpanded = false,
	dense = true,
}: FieldHelpProps) {
	const detailId = useId();
	const [expanded, setExpanded] = useState(defaultExpanded);
	const hasSummary = Boolean(summary);
	const hasDetail = Boolean(detail);

	if (!hasSummary && !hasDetail) return null;

	// Summary only — no progressive control needed.
	if (hasSummary && !hasDetail) {
		return (
			<Typography
				variant="caption"
				sx={{
					display: "block",
					mt: 0.25,
					color: "workspace.textMuted",
					lineHeight: 1.45,
					fontSize: dense ? undefined : "0.8125rem",
				}}
			>
				{summary}
			</Typography>
		);
	}

	// Detail only (or both): show summary when present; expand for detail.
	return (
		<Box sx={{ mt: 0.25 }}>
			{hasSummary ? (
				<Typography
					variant="caption"
					sx={{
						display: "block",
						color: "workspace.textMuted",
						lineHeight: 1.45,
					}}
				>
					{summary}
				</Typography>
			) : null}
			{hasDetail ? (
				<>
					<IconButton
						size="small"
						onClick={() => setExpanded((v) => !v)}
						aria-expanded={expanded}
						aria-controls={detailId}
						sx={{
							mt: 0.15,
							ml: -0.75,
							color: "workspace.textMuted",
							borderRadius: 1,
							"&:hover": { color: "workspace.blue" },
						}}
					>
						{expanded ? (
							<ExpandLessIcon sx={{ fontSize: 16 }} />
						) : (
							<ExpandMoreIcon sx={{ fontSize: 16 }} />
						)}
						<Typography
							component="span"
							variant="caption"
							sx={{ ml: 0.35, fontWeight: 600, letterSpacing: "0.02em" }}
						>
							{expanded ? "Less" : "Method"}
						</Typography>
					</IconButton>
					<Collapse in={expanded}>
						<Typography
							id={detailId}
							variant="caption"
							component="div"
							sx={{
								display: "block",
								color: "workspace.textMuted",
								lineHeight: 1.5,
								pl: 0.25,
								pb: 0.25,
							}}
						>
							{detail}
						</Typography>
					</Collapse>
				</>
			) : null}
		</Box>
	);
}
